/**
 * The running step's mark: the CV Health check's wave bent into a ring, as in
 * Material 3 Expressive's wavy circular indicator. A faint track circle with a
 * wavy arc on it; through each loop the arc grows to most of the ring, then
 * its tail catches up, while the whole ring turns once. Both platforms draw it
 * from these numbers, on the step mark's 20 pt grid:
 *   - the web strokes the closed ring as a dash (`stroke-dasharray` against
 *     `pathLength` 100) and turns it with CSS (global.css keeps the same
 *     timings; a test holds them together);
 *   - native redraws the arc every frame from `healthStepWaveAt`.
 * Under reduced motion both settle into the still arc `HEALTH_STEP_WAVE_REST`.
 */
export const HEALTH_STEP_WAVE = {
  radius: 6.6,
  amplitude: 0.75,
  lobes: 9,
  stroke: 1.7,
  trackStroke: 2,
  trackOpacity: 0.16,
  loopMs: 2400,
  /** Share of the ring the arc covers at its shortest, and at its longest. */
  minArc: 0.08,
  maxArc: 0.62,
  /** How far the tail has moved round the ring at the loop's half-way point. */
  midTail: 0.2,
  /** The still arc's share of the ring, under reduced motion. */
  restArc: 0.4,
  /** Points per full turn: about 0.6 pt apart, so the wave never shows facets. */
  samples: 72,
} as const;

/** CSS `ease-in-out`, cubic-bezier(0.42, 0, 0.58, 1), as a function of x in 0..1. */
export function healthStepWaveEase(x: number): number {
  'worklet';
  const cx = 3 * 0.42;
  const bx = 3 * (0.58 - 0.42) - cx;
  const ax = 1 - cx - bx;
  const cy = 0;
  const by = 3 * 1 - cy;
  const ay = 1 - cy - by;
  // Newton's method on x(t) = x; the curve is gentle, so a few steps do.
  let t = x;
  for (let i = 0; i < 8; i += 1) {
    const error = ((ax * t + bx) * t + cx) * t - x;
    const slope = (3 * ax * t + 2 * bx) * t + cx;
    if (Math.abs(error) < 1e-6 || Math.abs(slope) < 1e-6) break;
    t -= error / slope;
  }
  t = Math.min(1, Math.max(0, t));
  return ((ay * t + by) * t + cy) * t;
}

/**
 * Where the arc is at a point in the loop (0..1, wrapping): `turn` is how far
 * the ring has turned, `tail` where the arc starts on the ring and `span` how
 * much of it the arc covers, all in turns. The two halves ease separately, as
 * a CSS animation's keyframes do.
 */
export function healthStepWaveAt(progress: number): { turn: number; tail: number; span: number } {
  'worklet';
  const { minArc, maxArc, midTail } = HEALTH_STEP_WAVE;
  const p = progress - Math.floor(progress);
  const firstHalf = p < 0.5;
  const u = healthStepWaveEase(firstHalf ? p * 2 : (p - 0.5) * 2);
  return {
    turn: p,
    tail: firstHalf ? midTail * u : midTail + (1 - midTail) * u,
    span: firstHalf ? minArc + (maxArc - minArc) * u : maxArc - (maxArc - minArc) * u,
  };
}

/**
 * The wavy arc from `tail` covering `span` of the ring, with the ring turned
 * by `turn` (all in turns, 0 = 12 o'clock, clockwise), as SVG path data. The
 * wave belongs to the ring, so it turns with it.
 */
export function healthStepWaveArc(tail: number, span: number, turn = 0): string {
  'worklet';
  const { radius, amplitude, lobes, samples } = HEALTH_STEP_WAVE;
  const steps = Math.max(2, Math.ceil(span * samples));
  let d = '';
  for (let i = 0; i <= steps; i += 1) {
    const onRing = (tail + (span * i) / steps) * 2 * Math.PI - Math.PI / 2;
    const r = radius + amplitude * Math.sin(lobes * onRing);
    const angle = onRing + turn * 2 * Math.PI;
    d += `${i === 0 ? 'M' : 'L'}${(10 + r * Math.cos(angle)).toFixed(2)} ${(10 + r * Math.sin(angle)).toFixed(2)}`;
  }
  return d;
}

/** The whole ring, closed, from 12 o'clock: the web's dash runs along it. */
export const HEALTH_STEP_WAVE_RING = `${healthStepWaveArc(0, 1)}Z`;

/** The still arc under reduced motion: from 12 o'clock, `restArc` of the ring. */
export const HEALTH_STEP_WAVE_REST = healthStepWaveArc(0, HEALTH_STEP_WAVE.restArc);
